import moment from "moment-timezone";
import { DAY_AND_MONTH_FORMAT, IEffectiveFacilityPolicy } from "types";
import { filterPoliciesOverlappingPeriod } from "./filters";

export enum ClosureStatusType {
  NO_CLOSURES = "NO_CLOSURES",
  UPCOMING = "UPCOMING",
  CLOSED = "CLOSED",
}

export interface ClosureTooltipInfo {
  startDate: string;
  endDate: string;
  reason: string | null;
  isHiddenFromUsers: boolean;
}

export interface RelevantClosure {
  policy: IEffectiveFacilityPolicy;
  status: ClosureStatusType;
  tooltipInfo: ClosureTooltipInfo;
}

export interface BuildClosureInfoTextLinesOptions {
  /** Whether to include the header text "Office closed" */
  includeOfficeClosedHeader?: boolean;
  /** Whether to include the text "Hidden from users" */
  includeHiddenFromUsersText?: boolean;
}

export function getClosureStatusLabel(status: ClosureStatusType): string {
  const labels: Record<ClosureStatusType, string> = {
    [ClosureStatusType.NO_CLOSURES]: $localize`:@@closure|status|no-closures:No closures`,
    [ClosureStatusType.UPCOMING]: $localize`:@@closure|status|upcoming:Upcoming`,
    [ClosureStatusType.CLOSED]: $localize`:@@closure|status|closed:Closed`,
  };
  return labels[status];
}

function formatClosureDate(dateString: string, timezone?: string): string {
  const date = timezone ? moment(dateString).tz(timezone) : moment(dateString);
  return date.format(DAY_AND_MONTH_FORMAT);
}

/**
 * Builds tooltip info from a facility policy.
 * Formats dates with timezone and extracts reason from deny effects.
 */
export function buildTooltipInfo(policy: IEffectiveFacilityPolicy, timezone?: string): ClosureTooltipInfo {
  return {
    startDate: formatClosureDate(policy.startTime, timezone),
    endDate: policy.endTime
      ? formatClosureDate(policy.endTime, timezone)
      : $localize`:@@closure|no-end-date: no end date`,
    reason: policy.effects?.[0]?.options?.reason || null,
    isHiddenFromUsers: policyHasHideEffect(policy),
  };
}

export function buildClosureInfoTextLines(
  tooltipInfo: ClosureTooltipInfo,
  options: BuildClosureInfoTextLinesOptions = {},
): string[] {
  const { includeOfficeClosedHeader = false, includeHiddenFromUsersText = false } = options;
  const lines: string[] = [];

  if (includeOfficeClosedHeader) {
    lines.push($localize`:@@common|facility-office-closed:Office closed`);
  }

  if (tooltipInfo.startDate) {
    lines.push(`${$localize`:@@closure|start:Start`}: ${tooltipInfo.startDate}`);
  }
  if (!tooltipInfo.isHiddenFromUsers && tooltipInfo.endDate) {
    lines.push(`${$localize`:@@closure|end:End`}: ${tooltipInfo.endDate}`);
  }

  if (tooltipInfo.isHiddenFromUsers) {
    lines.push(`${$localize`:@@closure|end:End`}: ${$localize`:@@closure|no-end-date: no end date`}`);
  }

  if (tooltipInfo.reason?.length) {
    lines.push(`${$localize`:@@closure|reason:Reason`}: ${tooltipInfo.reason}`);
  }

  if (includeHiddenFromUsersText && tooltipInfo.isHiddenFromUsers) {
    lines.push($localize`:@@closure|hidden-from-users:Hidden from users`);
  }

  return lines;
}

export function buildClosureTooltipText(
  tooltipInfo: ClosureTooltipInfo,
  options: BuildClosureInfoTextLinesOptions = {},
): string {
  const lines = buildClosureInfoTextLines(tooltipInfo, options);

  return lines.join("\n");
}

export function getClosureStatus(closures: IEffectiveFacilityPolicy[] | null | undefined): ClosureStatusType {
  const relevant = getMostRelevantClosure(closures);
  return relevant?.status ?? ClosureStatusType.NO_CLOSURES;
}

/**
 * Gets the most relevant closure based on "now".
 * Finds either a currently active closure or the nearest upcoming one.
 */
export function getMostRelevantClosure(
  closures: IEffectiveFacilityPolicy[] | null | undefined,
  timezone?: string,
): RelevantClosure | null {
  if (!closures?.length) {
    return null;
  }

  const now = moment();

  // Find current closure (start <= now < end, or start <= now with no end for perpetual closures)
  const currentClosure = closures.find((policy) => {
    const start = moment(policy.startTime);
    const hasStarted = start.isSameOrBefore(now);
    // Treat missing endTime as unbounded (perpetual closure)
    const isOngoing = policy.endTime ? moment(policy.endTime).isAfter(now) : true;
    return hasStarted && isOngoing;
  });

  if (currentClosure) {
    return {
      policy: currentClosure,
      status: ClosureStatusType.CLOSED,
      tooltipInfo: buildTooltipInfo(currentClosure, timezone),
    };
  }

  // Find nearest upcoming closure (start > now)
  const upcomingClosure = closures
    .filter((policy) => moment(policy.startTime).isAfter(now))
    .sort((a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf())[0];

  if (upcomingClosure) {
    return {
      policy: upcomingClosure,
      status: ClosureStatusType.UPCOMING,
      tooltipInfo: buildTooltipInfo(upcomingClosure, timezone),
    };
  }

  return null;
}

/**
 * Gets the most relevant closure that overlaps with a specific time period.
 * Used when checking closures for a booking's time range instead of "now".
 */
export function getMostRelevantClosureForPeriod(
  closures: IEffectiveFacilityPolicy[] | null | undefined,
  periodStart: moment.MomentInput,
  periodEnd: moment.MomentInput,
  timezone?: string,
): RelevantClosure | null {
  if (!closures?.length) {
    return null;
  }

  const overlappingClosures = filterPoliciesOverlappingPeriod(closures, periodStart, periodEnd);

  const overlappingClosure = overlappingClosures.length ? overlappingClosures[0] : null;

  if (overlappingClosure) {
    return {
      policy: overlappingClosure,
      status: ClosureStatusType.CLOSED,
      tooltipInfo: buildTooltipInfo(overlappingClosure, timezone),
    };
  }

  return null;
}

/**
 * Checks if an indefinite closure is already effective from now (respecting office timezone).
 * Returns true if the closure start date has already passed in the office's timezone.
 */
export function isIndefiniteClosureEffective(startDate: string, timezone: string): boolean {
  const now = moment().tz(timezone);
  const closureStart = moment.tz(startDate, timezone);

  return closureStart.isSameOrBefore(now, "day");
}

/**
 * Checks if a policy has a "hide" effect enabled.
 */
export function policyHasHideEffect(policy: IEffectiveFacilityPolicy): boolean {
  return policy.effects?.some((effect) => effect.options?.hide === true) ?? false;
}

/**
 * Checks if closures with `hide: true` are effective for a given date range.
 * Used to determine if an office should be completely hidden from the UI.
 *
 * @param closures - Array of effective facility policies (closures)
 * @param periodStart - Start of the period to check (inclusive)
 * @param periodEnd - End of the period to check (inclusive)
 * @returns true if there's a hidden closure effective during the period
 */
export function hasEffectiveHiddenClosure(
  closures: IEffectiveFacilityPolicy[] | null | undefined,
  periodStart: moment.MomentInput,
  periodEnd: moment.MomentInput,
): boolean {
  if (!closures?.length) {
    return false;
  }

  const startMoment = moment(periodStart);
  const endMoment = moment(periodEnd);

  return closures.some((policy) => {
    // Check if this closure has hide effect
    if (!policyHasHideEffect(policy)) {
      return false;
    }

    // Check if closure overlaps with the period
    const closureStart = moment(policy.startTime);
    const closureEnd = policy.endTime ? moment(policy.endTime) : null;

    // Overlaps if: closure starts before period ends AND (closure ends after period starts OR is indefinite)
    const overlaps =
      closureStart.isSameOrBefore(endMoment, "day") && (!closureEnd || closureEnd.isSameOrAfter(startMoment, "day"));

    return overlaps;
  });
}

/**
 * Gets the set of office UUIDs that should be hidden for a specific day.
 * Uses a pre-fetched map of office policies to determine which offices have
 * hidden closures effective on that day.
 *
 * @param officePoliciesMap - Map of officeUuid to their effective policies
 * @param day - The specific day to check (YYYY-MM-DD format)
 * @returns Set of office UUIDs that should be hidden
 */
export function getHiddenOfficeUuidsForDay(
  officePoliciesMap: Map<string, IEffectiveFacilityPolicy[]>,
  day: string,
): Set<string> {
  const hiddenOfficeUuids = new Set<string>();

  officePoliciesMap.forEach((policies, officeUuid) => {
    if (hasEffectiveHiddenClosure(policies, day, day)) {
      hiddenOfficeUuids.add(officeUuid);
    }
  });

  return hiddenOfficeUuids;
}
